import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/data/blogPosts";
import { businessId, site } from "@/lib/site";

const Blog = () => {
  return (
    <>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${site.siteUrl}/` },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${site.siteUrl}/blog` }
          ]
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Demolition and Property Project Guides",
          "url": `${site.siteUrl}/blog`,
          "description": "Practical guides for interior demolition, renovation preparation, debris removal, and property projects in Northeast Wisconsin.",
          "publisher": { "@id": businessId }
        })}
      </script>

      {/* Hero */}
      <section
        className="relative py-16 md:py-20 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: "url('/images/broom_guitar.JPG')" }}
      >
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
        <div className="container relative z-10 text-center">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4">
            Demolition & Property Project Guides
          </h1>
          <p className="text-white/90 max-w-2xl mx-auto text-lg">
            Practical information for planning demolition, debris removal, and related property work in Wisconsin.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card key={post.slug} className="group overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-muted" />
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {post.readTime}
                    </span>
                  </div>
                  <h2 className="font-heading font-bold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </h2>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  >
                    Read More <ArrowRight className="w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-muted">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">Planning an Interior Demolition Project?</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Share your project type, property details, scope notes, and photos for review.
          </p>
          <Button asChild size="lg">
            <Link to="/book">Request an Estimate</Link>
          </Button>
        </div>
      </section>
    </>
  );
};

export default Blog;
